/** Central registry of React Query keys, one namespace per resource. */
export const queryKeys = {
  /**
   * Selection data from the common lookup API, one entry per type + filters.
   * The bare key is the prefix for invalidation after a CRUD change.
   */
  lookups: {
    all: ['lookups'] as const,
    for: (schoolId: number | null, type: string, params: unknown) =>
      ['lookups', type, schoolId, params] as const,
  },
  documents: {
    /** Configured document types (prefix, for invalidation). */
    types: ['documents', 'types'] as const,
    /** Every document list (prefix, for invalidation). */
    all: ['documents', 'list'] as const,
    forEntity: (entityType: string, entityId: number | null, params: unknown) =>
      ['documents', 'list', entityType, entityId, params] as const,
    versions: (documentId: number | null) => ['documents', 'versions', documentId] as const,
    activity: (documentId: number | null) => ['documents', 'activity', documentId] as const,
    permissions: (documentId: number | null) => ['documents', 'permissions', documentId] as const,
    /** The role to document type mapping behind the Roles & Permissions screen. */
    matrix: ['documents', 'permission-matrix'] as const,
  },
  dashboard: {
    /** The school dashboard's figures for one school and day. */
    summary: (schoolId: number | null, today: string) => ['dashboard', 'summary', schoolId, today] as const,
  },
  globalSettings: {
    /** Every settings and key format query (prefix, for invalidation). */
    all: ['global-settings'] as const,
    /** Settings of one scope: a branch, or `null` for the organization default. */
    settings: (branchId: number | null) => ['global-settings', 'settings', branchId] as const,
    keyFormats: (branchId: number | null) => ['global-settings', 'key-formats', branchId] as const,
  },
  letterheads: {
    /** Every letterhead query, lists and resolutions (prefix, for invalidation). */
    all: ['letterheads'] as const,
    /** `schoolId` is the school a branch list is for (null for the organization's). */
    list: (params: object, schoolId: number | null) => ['letterheads', 'list', params, schoolId] as const,
    item: (branch: boolean, letterheadTemplateId: number, schoolId: number | null) =>
      ['letterheads', 'item', branch, letterheadTemplateId, schoolId] as const,
    /** Per school: the header school decides which template applies. */
    resolve: (documentType: string, schoolId: number | null) => ['letterheads', 'resolve', documentType, schoolId] as const,
  },
  auditLogs: {
    /** Every audit list and row (prefix, for invalidation). */
    all: ['audit-logs'] as const,
    list: (query: unknown) => ['audit-logs', 'list', query] as const,
    detail: (auditLogId: number | null) => ['audit-logs', 'detail', auditLogId] as const,
  },
  academics: {
    // School-scoped lists: the bare key is the prefix for invalidation, the
    // `...For(schoolId)` key is the cached list of one school.
    years: ['academics', 'years'] as const,
    yearsFor: (schoolId: number | null) => ['academics', 'years', schoolId] as const,
    /** The caller's rights over past years in a school (`AcademicYearAccess`). */
    yearAccessFor: (schoolId: number | null) => ['academics', 'year-access', schoolId] as const,
    classes: ['academics', 'classes'] as const,
    classesFor: (schoolId: number | null) => ['academics', 'classes', schoolId] as const,
    sections: ['academics', 'sections'] as const,
    sectionsFor: (schoolId: number | null) => ['academics', 'sections', schoolId] as const,
    subjects: ['academics', 'subjects'] as const,
    subjectsFor: (schoolId: number | null) => ['academics', 'subjects', schoolId] as const,
    houses: ['academics', 'houses'] as const,
    housesFor: (schoolId: number | null) => ['academics', 'houses', schoolId] as const,
    classSections: ['academics', 'class-sections'] as const,
    classSectionsFor: (schoolId: number | null) => ['academics', 'class-sections', schoolId] as const,
    classSectionSubjects: ['academics', 'class-section-subjects'] as const,
    classSectionSubjectsFor: (schoolId: number | null) =>
      ['academics', 'class-section-subjects', schoolId] as const,
    /** Year rollover previews (prefix, for invalidation after a run, carry or finish). */
    rolloverPreviews: ['academics', 'rollover-preview'] as const,
    rolloverPreview: (schoolId: number | null, toYearId: number | null, fromYearId: number | null) =>
      ['academics', 'rollover-preview', schoolId, toYearId, fromYearId] as const,
  },
  administration: {
    // Education boards of a school: the bare key is the prefix for invalidation,
    // the `...For(schoolId)` key is the cached list of one school.
    educationBoards: ['administration', 'education-boards'] as const,
    educationBoardsFor: (schoolId: number | null) =>
      ['administration', 'education-boards', schoolId] as const,
    mediumsOfInstruction: ['administration', 'mediums-of-instruction'] as const,
    mediumsOfInstructionFor: (schoolId: number | null) =>
      ['administration', 'mediums-of-instruction', schoolId] as const,
  },
  school: {
    /** Schools of the organization (Foundation API). */
    list: ['school', 'list'] as const,
    /** Contacts of every school (prefix, for invalidation). */
    allContacts: ['school', 'contacts'] as const,
    contacts: (schoolId: number | null) => ['school', 'contacts', schoolId] as const,
  },
  students: {
    all: ['students', 'list'] as const,
    list: (schoolId: number | null, params: unknown) => ['students', 'list', schoolId, params] as const,
    /** Address, parents and previous schools of every student (prefix, for invalidation). */
    allDetails: ['students', 'details'] as const,
    details: (schoolId: number | null, studentId: number | null) =>
      ['students', 'details', schoolId, studentId] as const,
    admission: (schoolId: number | null, studentId: number | null) =>
      ['students', 'admission', schoolId, studentId] as const,
    contacts: ['students', 'contacts'] as const,
    previousSchools: ['students', 'previous-schools'] as const,
    parentInfo: ['students', 'parent-info'] as const,
    // Promotion masters: bare keys are prefixes for invalidation.
    categories: ['students', 'categories'] as const,
    categoriesFor: (schoolId: number | null) => ['students', 'categories', schoolId] as const,
    categoryAssignments: ['students', 'category-assignments'] as const,
    categoryAssignmentsFor: (schoolId: number | null, studentId: number | null) =>
      ['students', 'category-assignments', schoolId, studentId] as const,
    disciplineRecords: ['students', 'discipline-records'] as const,
    disciplineRecordsFor: (schoolId: number | null, studentId: number | null) =>
      ['students', 'discipline-records', schoolId, studentId] as const,
  },
  promotionRules: {
    // Bare keys are prefixes for invalidation.
    policies: ['promotion-rules', 'policies'] as const,
    policiesFor: (schoolId: number | null) => ['promotion-rules', 'policies', schoolId] as const,
    rules: ['promotion-rules', 'rules'] as const,
    rulesFor: (schoolId: number | null, policyId: number | null, includeHistory: boolean) =>
      ['promotion-rules', 'rules', schoolId, policyId, includeHistory] as const,
    fieldsFor: (schoolId: number | null) => ['promotion-rules', 'fields', schoolId] as const,
    validateFor: (schoolId: number | null, body: string) => ['promotion-rules', 'validate', schoolId, body] as const,
    evaluateFor: (schoolId: number | null, policyId: number | null, body: string) =>
      ['promotion-rules', 'evaluate', schoolId, policyId, body] as const,
    shuffleSettings: ['promotion-rules', 'shuffle-settings'] as const,
    shuffleSettingsFor: (schoolId: number | null, policyId: number | null) =>
      ['promotion-rules', 'shuffle-settings', schoolId, policyId] as const,
  },
  staff: {
    list: ['staff', 'list'] as const,
    listFor: (schoolId: number | null) => ['staff', 'list', schoolId] as const,
    /** Qualifications and experiences of every staff member (prefix, for invalidation). */
    allDetails: ['staff', 'details'] as const,
    details: (schoolId: number | null, staffId: number | null) =>
      ['staff', 'details', schoolId, staffId] as const,
    /** Activation check and status history of one staff member (prefix `lifecycle`, for invalidation). */
    lifecycle: ['staff', 'lifecycle'] as const,
    activationCheck: (schoolId: number | null, staffId: number | null) =>
      ['staff', 'lifecycle', 'activation', schoolId, staffId] as const,
    statusHistory: (schoolId: number | null, staffId: number | null) =>
      ['staff', 'lifecycle', 'history', schoolId, staffId] as const,
    /** Under `lifecycle`, so saves and status changes refresh it too. */
    completenessFor: (schoolId: number | null) =>
      ['staff', 'lifecycle', 'completeness', schoolId] as const,
    /** Assignments of every staff member (prefix, for invalidation) and of one. */
    assignments: ['staff', 'assignments'] as const,
    assignmentsFor: (schoolId: number | null, staffId: number | null) =>
      ['staff', 'assignments', schoolId, staffId] as const,
    /** Hostel blocks and bus routes a warden / route in-charge can be assigned to. */
    hostelBlocksFor: (schoolId: number | null) => ['staff', 'hostel-blocks', schoolId] as const,
    transportRoutesFor: (schoolId: number | null) => ['staff', 'transport-routes', schoolId] as const,
    /** Resolved field policy for one staff type / designation / employment type. */
    fieldPolicy: (schoolId: number | null, params: unknown) => ['staff', 'field-policy', schoolId, params] as const,
    // Staff masters of a school: the bare key is the prefix for invalidation.
    types: ['staff', 'types'] as const,
    typesFor: (schoolId: number | null) => ['staff', 'types', schoolId] as const,
    roles: (schoolId: number | null) => ['staff', 'roles', schoolId] as const,
    designations: ['staff', 'designations'] as const,
    designationsFor: (schoolId: number | null) => ['staff', 'designations', schoolId] as const,
    departments: ['staff', 'departments'] as const,
    departmentsFor: (schoolId: number | null) => ['staff', 'departments', schoolId] as const,
  },
  transport: {
    routes: ['transport', 'routes'] as const,
    routeNames: ['transport', 'route-names'] as const,
    stops: ['transport', 'stops'] as const,
    vehicles: (search: string) => ['transport', 'vehicles', search] as const,
    expenses: ['transport', 'expenses'] as const,
    drivers: ['transport', 'drivers'] as const,
    allocations: ['transport', 'allocations'] as const,
    attendance: ['transport', 'attendance'] as const,
    tripLogs: ['transport', 'trip-logs'] as const,
    fees: ['transport', 'fees'] as const,
    maintenance: ['transport', 'maintenance'] as const,
    alerts: ['transport', 'alerts'] as const,
  },
  fees: {
    heads: ['fees', 'heads'] as const,
    headTypes: ['fees', 'head-types'] as const,
    terms: ['fees', 'terms'] as const,
    structures: ['fees', 'structures'] as const,
    structureItems: (structureId: number | null) =>
      ['fees', 'structure-items', structureId] as const,
    allStructureItems: ['fees', 'structure-items', 'all'] as const,
    structureAdmissionItems: (structureId: number | null) =>
      ['fees', 'structure-admission-items', structureId] as const,
    payments: (query: unknown) => ['fees', 'payments', query] as const,
    adjustments: (structureId: number | null) => ['fees', 'adjustments', structureId] as const,
    allAdjustments: ['fees', 'adjustments', 'all'] as const,
    studentFees: (query: unknown) => ['fees', 'student-fees', query] as const,
    invoices: ['fees', 'invoices'] as const,
    scholarships: ['fees', 'scholarships'] as const,
    refunds: ['fees', 'refunds'] as const,
    expenses: ['fees', 'expenses'] as const,
    budgets: ['fees', 'budgets'] as const,
    savedReports: ['fees', 'saved-reports'] as const,
    paymentSettings: ['fees', 'payment-settings'] as const,
    paymentAccounts: ['fees', 'payment-accounts'] as const,
    dashboard: (filters: unknown) => ['fees', 'dashboard', filters] as const,
  },
  grading: {
    // School-scoped like `academics`: bare key = invalidation prefix.
    names: ['grading', 'names'] as const,
    namesFor: (schoolId: number | null) => ['grading', 'names', schoolId] as const,
    details: (gradeNameId: number) => ['grading', 'details', gradeNameId] as const,
    classAssignments: ['grading', 'class-assignments'] as const,
    classAssignmentsFor: (schoolId: number | null) => ['grading', 'class-assignments', schoolId] as const,
  },
  enrollment: {
    /** Enrollments, their subjects and promotions (prefix, for invalidation). */
    all: ['enrollment'] as const,
    list: (schoolId: number | null, params: unknown) => ['enrollment', 'list', schoolId, params] as const,
    subjects: (schoolId: number | null, enrollmentId: number | null) =>
      ['enrollment', 'subjects', schoolId, enrollmentId] as const,
    promotions: (schoolId: number | null, params: unknown) =>
      ['enrollment', 'promotions', schoolId, params] as const,
    availability: (schoolId: number | null, classId: number | null, yearId: number | null) =>
      ['enrollment', 'availability', schoolId, classId, yearId] as const,
  },
  assessment: {
    list: (params: unknown) => ['assessment', 'list', params] as const,
    roster: (assessmentId: number | null) => ['assessment', 'roster', assessmentId] as const,
  },
  examination: {
    exams: ['examination', 'exams'] as const,
    all: ['examination'] as const,
    subjects: (examId: number | null, classId: number | null, sectionId: number | null = null) =>
      ['examination', 'subjects', examId, classId, sectionId] as const,
    subjectMatrix: (examId: number | null, classId: number | null) =>
      ['examination', 'subject-matrix', examId, classId] as const,
    schedule: (examId: number | null, classId: number | null, sectionId: number | null = null) =>
      ['examination', 'schedule', examId, classId, sectionId] as const,
    attendanceSheet: (scheduleId: number | null) => ['examination', 'attendance-sheet', scheduleId] as const,
    marksSheet: (scheduleId: number | null) => ['examination', 'marks-sheet', scheduleId] as const,
    facultyPapers: (staffId: number | '') => ['examination', 'faculty-papers', staffId] as const,
    facultyMarksSheet: (scheduleId: number | null, staffId: number | '') =>
      ['examination', 'faculty-marks-sheet', scheduleId, staffId] as const,
    hallTicketsAll: ['examination', 'hall-tickets'] as const,
    hallTicketEligibility: (params: unknown) => ['examination', 'hall-tickets', 'eligibility', params] as const,
    hallTickets: (params: unknown) => ['examination', 'hall-tickets', 'sheet', params] as const,
    results: (params: unknown) => ['examination', 'results', params] as const,
    schemeResults: (params: unknown) => ['examination', 'schemeResults', params] as const,
    schemeRanks: (params: unknown) => ['examination', 'schemeRanks', params] as const,
    publishStatus: (params: unknown) => ['examination', 'publish-status', params] as const,
    schemes: ['examination', 'schemes'] as const,
    scheme: (schemeId: number | null) => ['examination', 'schemes', schemeId] as const,
    schemeSubjects: (schemeId: number | null, classId: number | null, sectionId: number | null = null) =>
      ['examination', 'schemes', schemeId, 'subjects', classId, sectionId] as const,
    schemeValidation: (schemeId: number | null) => ['examination', 'schemes', schemeId, 'validation'] as const,
    schemeTemplates: ['examination', 'scheme-templates'] as const,
    examTypes: ['examination', 'exam-types'] as const,
    questionPapers: ['examination', 'question-papers'] as const,
    examAttendance: ['examination', 'exam-attendance'] as const,
    gradingAssignments: ['examination', 'grading-assignments'] as const,
    reportCardRemarks: ['examination', 'report-card-remarks'] as const,
    transcriptRequests: ['examination', 'transcript-requests'] as const,
    revaluations: ['examination', 'revaluations'] as const,
    reportSummary: (examId: number | null, classId: number | null) =>
      ['examination', 'report-summary', examId, classId] as const,
    transcript: (studentId: number | null) => ['examination', 'transcript', studentId] as const,
    revaluationMark: (studentId: number | null, examId: number | null, subjectId: number | null) =>
      ['examination', 'revaluation-mark', studentId, examId, subjectId] as const,
  },
  attendance: {
    all: ['attendance'] as const,
    /** Every school's attendance settings (prefix, for invalidation). */
    settings: ['attendance', 'settings'] as const,
    settingsFor: (schoolId: number | null) => ['attendance', 'settings', schoolId] as const,
    matrix: (params: unknown) => ['attendance', 'matrix', params] as const,
    summary: (params: unknown) => ['attendance', 'summary', params] as const,
  },
  staffAttendance: {
    matrix: (params: unknown) => ['staff-attendance', 'matrix', params] as const,
  },
  homework: {
    list: ['homework', 'list'] as const,
  },
  timetable: {
    periods: ['timetable', 'periods'] as const,
    periodsFor: (schoolId: number | null) => ['timetable', 'periods', schoolId] as const,
    entries: (query: unknown) => ['timetable', 'entries', query] as const,
    rooms: ['timetable', 'rooms'] as const,
    roomsFor: (schoolId: number | null) => ['timetable', 'rooms', schoolId] as const,
    activities: ['timetable', 'activities'] as const,
    activitiesFor: (schoolId: number | null) => ['timetable', 'activities', schoolId] as const,
    bellSchedules: ['timetable', 'bell-schedules'] as const,
    bellSchedulesFor: (schoolId: number | null) => ['timetable', 'bell-schedules', schoolId] as const,
    settings: ['timetable', 'settings'] as const,
    settingsFor: (schoolId: number | null) => ['timetable', 'settings', schoolId] as const,
  },
  calendar: {
    // School-scoped like `academics`: bare key = invalidation prefix.
    events: ['calendar', 'events'] as const,
    eventsFor: (schoolId: number | null) => ['calendar', 'events', schoolId] as const,
  },
  library: {
    categories: ['library', 'categories'] as const,
    contributors: ['library', 'contributors'] as const,
    books: ['library', 'books'] as const,
    members: ['library', 'members'] as const,
    circulation: ['library', 'circulation'] as const,
    reservations: ['library', 'reservations'] as const,
    fines: ['library', 'fines'] as const,
    stockAdjustments: ['library', 'stock-adjustments'] as const,
  },
  hostel: {
    blocks: ['hostel', 'blocks'] as const,
    rooms: ['hostel', 'rooms'] as const,
    beds: ['hostel', 'beds'] as const,
    allocations: ['hostel', 'allocations'] as const,
    attendance: ['hostel', 'attendance'] as const,
    mealMenus: ['hostel', 'meal-menus'] as const,
    staff: ['hostel', 'staff'] as const,
    visitors: ['hostel', 'visitors'] as const,
    complaints: ['hostel', 'complaints'] as const,
    maintenance: ['hostel', 'maintenance'] as const,
  },
  admissions: {
    enquiries: ['admissions', 'enquiries'] as const,
    applicants: ['admissions', 'applicants'] as const,
    documents: ['admissions', 'documents'] as const,
    tests: ['admissions', 'tests'] as const,
    interviews: ['admissions', 'interviews'] as const,
    counselling: ['admissions', 'counselling'] as const,
    selections: ['admissions', 'selections'] as const,
    approvals: ['admissions', 'approvals'] as const,
    feePayments: ['admissions', 'fee-payments'] as const,
    admissionFees: ['admissions', 'admission-fees'] as const,
    admissionFee: (applicantId: number | null) => ['admissions', 'admission-fees', applicantId] as const,
    waitingList: ['admissions', 'waiting-list'] as const,
  },
  portal: {
    myApplications: ['portal', 'my-applications'] as const,
  },
  health: {
    records: ['health', 'records'] as const,
    incidents: ['health', 'incidents'] as const,
  },
  sports: {
    sports: ['sports', 'sports'] as const,
    teams: ['sports', 'teams'] as const,
    coaches: ['sports', 'coaches'] as const,
    players: ['sports', 'players'] as const,
    trainingSchedules: ['sports', 'training-schedules'] as const,
    matches: ['sports', 'matches'] as const,
    attendance: ['sports', 'attendance'] as const,
    performance: ['sports', 'performance'] as const,
    tournaments: ['sports', 'tournaments'] as const,
    achievements: ['sports', 'achievements'] as const,
    facilities: ['sports', 'facilities'] as const,
  },
  facility: {
    buildings: ['facility', 'buildings'] as const,
    facilities: ['facility', 'facilities'] as const,
    bookings: ['facility', 'bookings'] as const,
    maintenance: ['facility', 'maintenance'] as const,
    cleaning: ['facility', 'cleaning'] as const,
    security: ['facility', 'security'] as const,
  },
  communication: {
    announcements: ['communication', 'announcements'] as const,
    notices: ['communication', 'notices'] as const,
    outboundMessages: ['communication', 'outbound-messages'] as const,
    directMessages: ['communication', 'direct-messages'] as const,
    events: ['communication', 'events'] as const,
    feedback: ['communication', 'feedback'] as const,
    emergencyAlerts: ['communication', 'emergency-alerts'] as const,
    templates: ['communication', 'templates'] as const,
  },
  facultyPortal: {
    lessonPlans: ['faculty-portal', 'lesson-plans'] as const,
    assignments: ['faculty-portal', 'assignments'] as const,
    submissions: ['faculty-portal', 'submissions'] as const,
    leaveRequests: ['faculty-portal', 'leave-requests'] as const,
  },
  parentPortal: {
    leaveRequests: ['parent-portal', 'leave-requests'] as const,
  },
  principalPortal: {
    disciplineRecords: ['principal-portal', 'discipline-records'] as const,
    parentIssues: ['principal-portal', 'parent-issues'] as const,
  },
} as const;
